<template>
    <div>
        <div class="chart-wrapper" :class="`${type}-chart-wrapper`">
            <canvas class="chart" :id="id"></canvas>
            <canvas class="print-chart" :id="printId"></canvas>
            <img v-if="chartImage" class="print-chart-image" :src="chartImage">
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js';
import { isInViewport } from 'src/util/helpers';
import Annotations from 'chartjs-plugin-annotation';
import cloneDeep from 'lodash/cloneDeep';
import eventBus from 'src/util/eventBus';

Chart.defaults.global.defaultFontFamily = 'Roboto';

export default {
    props: {
        type: {
            type: String,
            required: true,
        },
        tooltips: {
            type: Boolean,
            default: true,
        },
        displayY: {
            type: Boolean,
            default: true,
        },
        displayX: {
            type: Boolean,
            default: true,
        },
        displayYGrid: {
            type: Boolean,
            default: true,
        },
        displayXGrid: {
            type: Boolean,
            default: true,
        },
        displayLegend: {
            type: Boolean,
            default: true,
        },
        suggestedYMax: {
            type: Number,
            default: null,
        },
        borderColor: {
            type: String,
            default: 'rgba(76, 175, 80, 1)',
        },
        backgroundColor: {
            type: String,
            default: 'rgba(76, 175, 80, .6)',
        },
        chartData: {
            type: Array,
            default: () => [],
        },
        annotation: {
            type: Object,
            default: () => ({}),
        },
        config: {
            type: Object,
            default: () => ({}),
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        datasets: {
            type: Array,
            required: true,
        },
        labels: {
            type: Array,
            default: () => [],
        },
        animate: {
            type: Boolean,
            default: null,
        },
    },
    data() {
        const id = 'chart-' + this._uid;

        return {
            id,
            printId: 'print-' + id,
            chart: null,
            printChart: null,
            printing: false,
        };
    },
    mounted() {
        this.setupPrinterListeners();

        const $el = this.$el.querySelector(`#${this.id}`);

        const callback = (entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.renderChart();

                    this.loadingObserver.disconnect();
                }
            });
        };

        this.loadingObserver = new IntersectionObserver(callback, {
            threshold: 0.0,
        });

        this.loadingObserver.observe($el);
    },
    destroyed() {
        eventBus.$off('beforeprint');
        eventBus.$off('afterprint');
    },
    methods: {
        setupPrinterListeners() {
            eventBus.$on('beforeprint', () => {
                if (!this.chart) {
                    this.printing = true;
                    this.renderChart();
                }

                if (this.loadingObserver) {
                    this.loadingObserver.disconnect();
                }
            });

            eventBus.$on('afterprint', () => {
                this.printing = false;
            });
        },
        renderChart() {
            if (this.chart) {
                this.chart.destroy();
            }

            if (this.printChart) {
                this.printChart.destroy();
            }

            this.chart = this.initChart(this.id);
        },
        initChart(id) {
            const canvas = document.getElementById(id);

            if (canvas === null) {
                return null;
            }

            const ctx = canvas.getContext('2d');

            return new Chart(ctx, cloneDeep(this.chartConfig));
        },
        isInViewport() {
            return isInViewport(this.$el);
        },
    },
    computed: {
        chartConfig() {
            return {
                type: this.type,
                data: {
                    labels: this.labels,
                    datasets: this.datasets,
                },
                options: {
                    ...this.chartOptions,
                    ...this.options,
                },
                ...this.config,
                plugins: this.plugins,
            };
        },
        plugins() {
            return [
                {
                    afterRender: (chart, options) => {
                        if (chart.canvas.id.indexOf('print') === -1) {
                            this.printChart = this.initChart(this.printId);
                        }
                    },
                },
                {
                    beforeInit: (chart, options) => {
                        chart.options.plugins.$el = this.$el;

                        if (chart.canvas.id.indexOf('print') === -1) {
                            return;
                        }

                        chart.options.plugins.mode = 'print';
                        chart.options.animation = false;
                        chart.options.tooltips.enabled = false;
                        chart.options.legend.labels.fontColor = '#000';
                    },
                },
                ...(this.config.plugins || []),
            ];
        },
        chartImage() {
            if (!this.printChart || !this.printChart.canvas) {
                return false;
            }

            return this.printChart.toBase64Image();
        },
        chartOptions() {
            return {
                animation: this.animation,
                maintainAspectRatio: false,
                cutoutPercentage: this.cutoutPercentage,
                tooltips: {
                    enabled: this.tooltips,
                    intersect: false,
                    xPadding: 12,
                    yPadding: 12,
                    mode: 'index',
                    displayColors: false,
                    backgroundColor: '#fff',
                    titleFontColor: '#272727',
                    bodyFontColor: '#272727',
                },
                legend: {
                    display: this.displayLegend,
                    reverse: true,
                    labels: {
                        fontColor: 'rgba(255, 255, 255, .5)',
                        boxWidth: 12,
                    },
                },
            };
        },
        animation() {
            if (this.animate === false) {
                return false;
            }

            if (this.printing) {
                return false;
            }

            const options = {
                easing: 'easeOutQuint',
            };

            if (this.animate === true) {
                return options;
            }

            if (this.isInViewport()) {
                return options;
            }

            return false;
        },
    },
    watch: {
        datasets: function() {
            this.renderChart();
        },
    },
};
</script>

